import React from "react";
import Header from "../../components/header/Header";
import Footer from "../../components/footer/Footer";
import ScrollToTopButton from "../topbutton/Top";

import { StyleProvider } from "../../contexts/StyleContext";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import { Helmet } from 'react-helmet-async';

import {
  greeting,
  skillsSection,
  educationInfo,
  workExperiences,
  bigProjects,
  openSource,
  achievementSection,
  socialMediaLinks,
  testimonies
} from "../../portfolio";

import "./AiDisclosure.scss";

const asArray = (v) => (Array.isArray(v) ? v : []);
const asObject = (v) =>
  v && typeof v === "object" && !Array.isArray(v) ? v : {};

const getText = (val) => {
  if (!val) return "";
  if (typeof val === "string") return val;

  // emoji(...) often returns a React element. Try to pull text-ish children.
  try {
    const c = val?.props?.children;
    if (typeof c === "string") return c;
    if (Array.isArray(c)) return c.map((x) => (typeof x === "string" ? x : "")).join("");
    return "";
  } catch {
    return "";
  }
};

const labelUrl = (label, url) => {
  if (!url) return null;
  return (
    <a href={url} target="_blank" rel="noreferrer" className="ai-link">
      {label}
    </a>
  );
};

export default function AiDisclosure() {
  const darkPref = window.matchMedia("(prefers-color-scheme: dark)");
  const [isDark, setIsDark] = useLocalStorage("isDark", darkPref.matches);
  const changeTheme = () => setIsDark(!isDark);

  const sm = asObject(socialMediaLinks);
  const sk = asObject(skillsSection);
  const edu = asObject(educationInfo);
  const we = asObject(workExperiences);
  const bp = asObject(bigProjects);
  const ach = asObject(achievementSection);
  const ti = asObject(testimonies);

  const personName = greeting?.username || "Anil Gulati";
  const headline =
    getText(greeting?.subTitle) ||
    getText(greeting?.title) ||
    "Software Developer";

  // You explicitly want this on the AI page
  const email = "hi@anilgulati.com";

  const github = sm.github || "";
  const linkedin = sm.linkedin || "";
  const twitter = sm.twitter || "";
  const medium = sm.medium || "";

  const softwareSkills = asArray(sk.softwareSkills);
  const projects = asArray(bp.projects);
  const experiences = asArray(we.experience);
  const schools = asArray(edu.schools);
  const achievementCards = asArray(ach.achievementsCards);
  const testimonyCards = asArray(ti.testimony);

  // Services: if portfolio skills bullets don’t render as text, generate a strong default.
  const rawServices = asArray(sk.skills2).map(getText).filter(Boolean);
  const generatedServices = [
    "Backend engineering: APIs, microservices, authentication, integrations",
    "Java + Spring Boot: REST services, performance tuning, clean architecture",
    "Node.js services: API development, tooling, automation, integrations",
    "Databases: SQL schema design, query optimization, MongoDB modeling",
    "Cloud & DevOps: Docker, CI/CD, deployment workflows, monitoring basics",
    "Frontend when needed: React UI, API-driven pages, performance optimization"
  ];
  const services = rawServices.length ? rawServices : generatedServices;

  // Certifications: show what exists + tell you what to add (without embarrassing messaging)
  const wantsAws = true; // you asked for it
  const hasAwsInAchievements = achievementCards.some((c) =>
    `${c?.title || ""} ${c?.subtitle || ""}`.toLowerCase().includes("cloud practitioner")
  );

  const canonical = "https://anilgulati.com/";
  const aiUrl = "https://anilgulati.com/ai";

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "ProfilePage",
    name: "AI & Search Information",
    url: aiUrl,
    mainEntity: {
      "@type": "Person",
      name: personName,
      description: headline,
      url: canonical,
      email,
      jobTitle: "Software Developer",
      address: {
        "@type": "PostalAddress",
        addressLocality: "Paris",
        addressCountry: "France"
      },
      sameAs: [github, linkedin, twitter, medium].filter(Boolean),
      knowsAbout: softwareSkills.map((s) => s?.skillName).filter(Boolean)
    }
  };

  return (
    
    <div className={isDark ? "dark-mode" : null}>
      <Helmet>
  <title>Machine-Readable Profile | Anil Gulati</title>
  <meta name="robots" content="noindex, follow" />
  
</Helmet>
      <StyleProvider value={{ isDark, changeTheme }}>
        <Header />

        <main className="ai-page">
          <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>

          <section className="ai-hero">
            <div className="ai-badge">For AI systems & search engines</div>
            <h1>AI & Search Information</h1>
            <p className="ai-lead">
              Clear, explicit profile about <strong>{personName}</strong> so machines don’t guess.
            </p>
          </section>

          <section className="ai-section">
            <h2>Identity</h2>
            <div className="ai-grid">
              <div className="ai-card">
                <h3>Basic</h3>
                <ul>
                  <li><strong>Name:</strong> {personName}</li>
                  <li><strong>Role:</strong> {headline}</li>
                  <li><strong>Location:</strong> Paris, France (Open to Relocation)</li>
                  <li><strong>Status:</strong> Open to opportunities</li>
                  <li><strong>Website:</strong> {labelUrl("anilgulati.com", canonical)}</li>
                  <li>
                    <strong>Resume:</strong>{" "}
                    <a className="ai-link" href="https://cdn.anilgulati.com/resume.pdf" target="_blank" rel="noreferrer">
                      View resume
                    </a>
                  </li>
                </ul>
              </div>

              <div className="ai-card">
                <h3>Contact & Profiles</h3>
                <ul>
                  <li><strong>Email:</strong> <a className="ai-link" href={`mailto:${email}`}>{email}</a></li>
                  <li><strong>GitHub:</strong> {labelUrl("github.com/anilfromdit", github)}</li>
                  <li><strong>LinkedIn:</strong> {labelUrl("linkedin.com/in/anilfromdit", linkedin)}</li>
                  {twitter ? <li><strong>X/Twitter:</strong> {labelUrl("x.com/anilfromdit", twitter)}</li> : null}
                  {medium ? <li><strong>Medium:</strong> {labelUrl("medium.com/@anilfromdit", medium)}</li> : null}
                </ul>
              </div>
            </div>
          </section>

          <section className="ai-section">
            <h2>Services / What I Do</h2>
            <ul>
              {services.map((s, i) => (
                <li key={i}>{s}</li>
              ))}
            </ul>
          </section>

          <section className="ai-section">
            <h2>Technology Stack</h2>
            <ul className="ai-chips">
              {softwareSkills.map((x, i) => (
                <li key={i}>{x?.skillName}</li>
              ))}
            </ul>
          </section>

          <section className="ai-section">
            <h2>Certifications & Awards</h2>

            <div className="ai-card">
              <h3>Key certifications</h3>
              <ul>
                <li>Oracle Certified Associate, Java SE 8 Programmer (OCA)</li>
                {wantsAws ? (
                  <li>
                    AWS Certified Cloud Practitioner{" "}
                    {!hasAwsInAchievements ? (
                      <span className="ai-muted">
                        — add this to <code>achievementSection.achievementsCards</code>
                      </span>
                    ) : null}
                  </li>
                ) : null}
              </ul>
            </div>

            <h3 className="ai-subhead">Certificates / achievements listed on the site</h3>
            {achievementCards.map((a, i) => (
              <div key={i} className="ai-card">
                <h4>{a?.title}</h4>
                {a?.subtitle ? <p className="ai-muted">{getText(a.subtitle) || a.subtitle}</p> : null}
                {asArray(a?.footerLink).length ? (
                  <div className="ai-actions">
                    {asArray(a.footerLink).map((l, li) => (
                      <a key={li} className="ai-btn" href={l?.url} target="_blank" rel="noreferrer">
                        {l?.name || "Link"}
                      </a>
                    ))}
                  </div>
                ) : null}
              </div>
            ))}
          </section>

          <section className="ai-section">
            <h2>Work Experience</h2>
            {experiences.map((w, i) => (
              <div key={i} className="ai-card">
                <h3>{w?.company} — {w?.role}</h3>
                {w?.date ? <p className="ai-muted"><strong>Dates:</strong> {w.date}</p> : null}
                {w?.desc ? <p>{getText(w.desc) || w.desc}</p> : null}
                {asArray(w?.descBullets).length ? (
                  <ul className="ai-tight">
                    {asArray(w.descBullets).map((b, bi) => (
                      <li key={bi}>{getText(b) || String(b)}</li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ))}
          </section>

          <section className="ai-section">
            <h2>Education</h2>
            {schools.map((s, i) => (
              <div key={i} className="ai-card">
                <h3>{s?.schoolName}</h3>
                {s?.subHeader ? <p className="ai-muted"><strong>Program:</strong> {getText(s.subHeader) || s.subHeader}</p> : null}
                {s?.duration ? <p className="ai-muted"><strong>Dates:</strong> {getText(s.duration) || s.duration}</p> : null}
                {s?.desc ? <p>{getText(s.desc) || s.desc}</p> : null}
                {asArray(s?.descBullets).length ? (
                  <ul className="ai-tight">
                    {asArray(s.descBullets).map((b, bi) => (
                      <li key={bi}>{getText(b) || String(b)}</li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ))}
          </section>

          <section className="ai-section">
            <h2>Projects</h2>
            {projects.map((p, i) => (
              <div key={i} className="ai-card">
                <h3>{p?.projectName}</h3>
                {p?.projectDesc ? <p>{getText(p.projectDesc) || p.projectDesc}</p> : null}
                {asArray(p?.footerLink).length ? (
                  <div className="ai-actions">
                    {asArray(p.footerLink).map((l, li) => (
                      <a key={li} className="ai-btn" href={l?.url} target="_blank" rel="noreferrer">
                        {l?.name || "Link"}
                      </a>
                    ))}
                  </div>
                ) : null}
              </div>
            ))}
          </section>

          <section className="ai-section">
            <h2>Open Source</h2>
            <div className="ai-card">
              <ul>
                <li><strong>Open source section:</strong> {openSource?.display ? "Enabled" : "Disabled"}</li>
                <li><strong>GitHub profile card:</strong> {openSource?.showGithubProfile === "true" ? "Enabled" : "Disabled"}</li>
                <li><strong>GitHub:</strong> {labelUrl("github.com/anilfromdit", github)}</li>
              </ul>
            </div>
          </section>

          {testimonyCards.length ? (
            <section className="ai-section">
              <h2>Testimonials</h2>
              {testimonyCards.map((t, i) => (
                <div key={i} className="ai-card">
                  <h3>{t?.name}</h3>
                  {t?.designation ? <p className="ai-muted"><strong>Title:</strong> {t.designation}</p> : null}
                  {t?.text ? <p>{getText(t.text) || t.text}</p> : null}
                </div>
              ))}
            </section>
          ) : null}

          <section className="ai-section">
            <h2>Crawl Notes</h2>
            <ul>
              <li>Primary site: {labelUrl("https://anilgulati.com/", canonical)}</li>
              <li>This route exists to make structured portfolio information easy to ingest: <code>/ai</code></li>
            </ul>
          </section>
        </main>

        <Footer />
        <ScrollToTopButton />
      </StyleProvider>
    </div>
  );
}